import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

const TOKEN_KEYS = ['keycloak_access_token', 'keycloak_id_token'] as const;
type TrackedKey = typeof TOKEN_KEYS[number];

@Injectable({ providedIn: 'root' })
export class UserChangeDetectorService {
  private readonly platformId = inject(PLATFORM_ID);

  private started = false;
  private reloading = false;
  private currentSub: string | null = null;
  private reloadTimer: ReturnType<typeof setTimeout> | null = null;

  start(): void {
    if (this.started) return;
    if (!isPlatformBrowser(this.platformId)) return;

    this.started = true;
    this.currentSub = this.readSubFromStorage();

    window.addEventListener('storage', this.onStorage);
    window.addEventListener('pagehide', this.onPageHide);
  }

  stop(): void {
    if (!this.started) return;
    if (!isPlatformBrowser(this.platformId)) return;

    window.removeEventListener('storage', this.onStorage);
    window.removeEventListener('pagehide', this.onPageHide);
    this.started = false;

    if (this.reloadTimer) {
      clearTimeout(this.reloadTimer);
      this.reloadTimer = null;
    }
  }

  private readonly onStorage = (event: StorageEvent): void => {
    if (!event.key) return;
    if (!(TOKEN_KEYS as readonly string[]).includes(event.key)) return;

    const nextSub = this.readSubFromStorage();

    if (nextSub === this.currentSub) {
      return;
    }

    const wasAuthenticated = this.currentSub !== null;
    const isAuthenticated = nextSub !== null;

    this.currentSub = nextSub;

    if (wasAuthenticated || isAuthenticated) {
      this.scheduleReload();
    }
  };

  private readonly onPageHide = (): void => {
    if (this.reloadTimer) {
      clearTimeout(this.reloadTimer);
      this.reloadTimer = null;
    }
  };

  private scheduleReload(): void {
    if (this.reloading) return;
    this.reloading = true;

    if (this.reloadTimer) clearTimeout(this.reloadTimer);

    this.reloadTimer = setTimeout(() => {
      try {
        window.location.reload();
      } catch {
        this.reloading = false;
      }
    }, 150);
  }

  private readSubFromStorage(): string | null {
    try {
      for (const key of TOKEN_KEYS) {
        const token = localStorage.getItem(key);
        if (!token) continue;
        const sub = this.extractSub(token);
        if (sub) return sub;
      }
    } catch {
      return null;
    }
    return null;
  }

  private extractSub(jwt: string): string | null {
    const parts = jwt.split('.');
    if (parts.length < 2) return null;

    try {
      const payload = parts[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padLen = (4 - (base64.length % 4)) % 4;
      const padded = base64 + '='.repeat(padLen);
      const decoded = atob(padded);
      const json = decodeURIComponent(
        decoded
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const claims = JSON.parse(json);
      return typeof claims?.sub === 'string' ? claims.sub : null;
    } catch {
      return null;
    }
  }
}
